/**
 * comboDetector — hidden combos, isolated from the core input loop.
 *
 * It observes the raw key stream (fed from main.ts's onDown/onUp, alongside the
 * InputController and TapTempo) and, on a match, writes ONLY to the paramBus —
 * `combo.<name>` → 1 / 0. The scene subscribes and reacts; the detector never
 * touches a uniform, and the InputController never sees a combo. New combos are
 * added by appending to `comboConfig`, not by editing this file.
 *
 * Two matchers, both reading the same declarative registry:
 *   • sequence — a rolling buffer of recent keydowns (key + timestamp). On each
 *     keydown, the buffer tail is tested against every sequence combo; a match
 *     within SEQUENCE_WINDOW_MS fires a one-off (set 1, auto-release to 0 after
 *     SEQUENCE_FLASH_MS via an injected timer — same pattern as TapTempo).
 *   • chord — a live set of held keys, updated on down/up. Whenever the set
 *     fully contains a chord combo's keys the param is 1; the moment any lifts
 *     it drops to 0.
 *
 * Time and timers are injected (default to performance.now / setTimeout) so the
 * matching logic is unit-testable with synthetic timestamps.
 */

import { ParamBus } from '../state/paramBus';
import { ComboDef, comboConfig, comboParam } from '../effects/comboConfig';

/** Timer injection points — mirrors TapTempo so timers stay testable. */
export type ScheduleFn = (handler: () => void, ms: number) => unknown;
export type CancelFn = (id: unknown) => void;

/** A sequence must complete (first→last key) within this span. */
export const SEQUENCE_WINDOW_MS = 2500;
/** How long a fired sequence combo stays lit before auto-releasing. */
export const SEQUENCE_FLASH_MS = 2400;
/** Cap on the rolling keydown buffer. */
const BUFFER_MAX = 16;

interface Stamp {
  readonly key: string;
  readonly t: number;
}

export class ComboDetector {
  private readonly sequences: readonly ComboDef[];
  private readonly chords: readonly ComboDef[];
  private buffer: Stamp[] = [];
  private readonly held = new Set<string>();
  private readonly flashTimers = new Map<string, unknown>();

  constructor(
    private readonly bus: ParamBus,
    config: readonly ComboDef[] = comboConfig,
    private readonly now: () => number = defaultNow,
    // Wrap the globals in arrows so they keep their `window` binding when
    // invoked as `this.setTimer(...)` — a bare reference throws "Illegal
    // invocation" in the browser (same gotcha TapTempo documents).
    private readonly setTimer: ScheduleFn = (fn, ms) => setTimeout(fn, ms),
    private readonly clearTimer: CancelFn = (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
  ) {
    this.sequences = config.filter((c) => c.kind === 'sequence');
    this.chords = config.filter((c) => c.kind === 'chord');
  }

  /** Currently-held keys (for tests / debugging). */
  heldKeys(): string[] {
    return [...this.held];
  }

  /** Feed a keydown. `t` defaults to the injected clock. */
  keyDown(key: string, t: number = this.now()): void {
    this.held.add(key);
    this.pushSequence(key, t);
    this.matchSequences(t);
    this.matchChords();
  }

  /** Feed a keyup. */
  keyUp(key: string): void {
    this.held.delete(key);
    this.matchChords();
  }

  private pushSequence(key: string, t: number): void {
    this.buffer.push({ key, t });
    // Drop anything too old to still be part of a sequence ending now.
    const cutoff = t - SEQUENCE_WINDOW_MS;
    while (this.buffer.length && this.buffer[0].t < cutoff) this.buffer.shift();
    if (this.buffer.length > BUFFER_MAX) this.buffer.splice(0, this.buffer.length - BUFFER_MAX);
  }

  private matchSequences(t: number): void {
    for (const combo of this.sequences) {
      const n = combo.keys.length;
      if (this.buffer.length < n) continue;
      const tail = this.buffer.slice(this.buffer.length - n);
      const ordered = tail.every((s, i) => s.key === combo.keys[i]);
      if (!ordered) continue;
      if (t - tail[0].t > SEQUENCE_WINDOW_MS) continue;
      this.fireSequence(combo);
    }
  }

  private fireSequence(combo: ComboDef): void {
    // Consume the buffer so the same keys can't immediately re-trigger.
    this.buffer = [];
    const param = comboParam(combo.name);
    this.bus.set(param, 1);
    const existing = this.flashTimers.get(combo.name);
    if (existing) this.clearTimer(existing);
    this.flashTimers.set(
      combo.name,
      this.setTimer(() => {
        this.flashTimers.delete(combo.name);
        this.bus.set(param, 0);
      }, SEQUENCE_FLASH_MS),
    );
  }

  private matchChords(): void {
    for (const combo of this.chords) {
      const allDown = combo.keys.every((k) => this.held.has(k));
      this.bus.set(comboParam(combo.name), allDown ? 1 : 0);
    }
  }
}

function defaultNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
